/**
 * Auth Store
 *
 * Extends module-core's authStore$ with web-specific functionality
 * Integrates with NextAuth session for web platform
 *
 * Following mobile's pattern: module-auth's authStore$ is a direct reference
 * to module-core's authStore$, cast with extended types.
 * This ensures a SINGLE observable instance across both modules, so when
 * module-core clears token (e.g. on refresh token failure), module-auth
 * and all consumers (cart, quote, etc.) see the change immediately.
 */

import { type Observable } from '@legendapp/state';
import { authStore$ as coreAuthStore$, type AuthStore as CoreAuthStore } from '@kidslabs/module-core';
import type {
    CustomerToken,
    CustomerProfile,
    InitValidatePhoneParams,
    RequestValidatePhoneResponse,
    ConfirmRequestedValidationPhoneResponse,
    NotificationChannel,
    ValidatePhoneType,
    AuthenticationStep,
    AuthMode,
} from '../types';
import { clearIdentityAuthData } from './identity-auth.store';

/**
 * Pending registration form data — persisted across step transitions
 * so user input is preserved when navigating back from errors.
 * Only cleared on complete authentication success.
 */
export interface PendingRegistrationInput {
    name?: string;
    email?: string;
    referralCode?: string;
    isAgreeTermAndPolicy?: boolean;
}

/**
 * Extended Auth Store with web-specific fields
 */
export interface AuthStoreExtended extends CoreAuthStore {
    customerToken?: CustomerToken;
    customerProfile?: CustomerProfile;
    // Web-specific fields
    sessionExpiresAt?: number;
    isSessionSyncing?: boolean;
    lastSessionSync?: number;
    isAuthenticated?: boolean;
    isLoading?: boolean;
    error?: string;
    initValidatePhoneData?: InitValidatePhoneParams;
    requestValidatePhoneResponse?: RequestValidatePhoneResponse;
    confirmRequestedValidationPhone?: ConfirmRequestedValidationPhoneResponse;
    currentOtp?: string;
    notificationChannel?: NotificationChannel;
    validatePhoneType?: ValidatePhoneType;
    otpStartTime?: number;
    step: AuthenticationStep;
    /** Whether user entered via Login or Register path */
    authMode: AuthMode;
    /** Registration form data preserved across step transitions */
    pendingRegistrationInput?: PendingRegistrationInput;
}

// Re-export the core type for convenience
export type { CoreAuthStore as AuthStore };

/**
 * Auth store observable - DIRECT REFERENCE to module-core's authStore$
 *
 * This is the same observable instance as module-core's authStore$,
 * cast with extended web-specific types.
 * Any mutation here is visible in module-core and vice versa.
 */
export const authStore$: Observable<AuthStoreExtended> = coreAuthStore$ as Observable<AuthStoreExtended>;

/**
 * Clear auth data - for logout
 */
export const clearAuthData = (): void => {
    authStore$.customerToken.set(undefined);
    authStore$.customerProfile.set(undefined);
    authStore$.isAuthenticated.set(false);
    authStore$.isLoading.set(false);
    authStore$.error.set(undefined);
    authStore$.sessionExpiresAt.set(undefined);
    authStore$.lastSessionSync.set(undefined);
};

/**
 * Clear only OTP-related data — for retry OTP / expired OTP scenarios.
 * Preserves: phoneNumber, notificationChannel, validatePhoneType, authMode.
 */
export const clearOtpSessionData = (): void => {
    authStore$.requestValidatePhoneResponse.set(undefined);
    authStore$.confirmRequestedValidationPhone.set(undefined);
    authStore$.currentOtp.set(undefined);
    authStore$.otpStartTime.set(undefined);
    // Note: otpAttemptCount and isOtpLocked are persisted via localStorage
    // (otp-attempt-tracker.ts) and reset there — not in this store.
};

/**
 * Clear OTP + channel data — for back-to-channel-selection scenarios.
 * Preserves: phoneNumber, validatePhoneType, authMode.
 */
export const clearChannelAndOtpData = (): void => {
    clearOtpSessionData();
    authStore$.notificationChannel.set(undefined);
};

/**
 * Clear all login session data — for complete reset (logout, mode toggle, post-login success).
 */
export const clearLoginSessionData = (): void => {
    authStore$.initValidatePhoneData.set(undefined);
    clearChannelAndOtpData();
    authStore$.validatePhoneType.set(undefined);
    authStore$.step.set('EnterPhoneNumber');
    authStore$.authMode.set('login');
    authStore$.pendingRegistrationInput.set(undefined);
    clearIdentityAuthData();
};

/**
 * Reset to phone entry step — preserves phone number and registration form data.
 * Used when OTP session is invalidated or errors require restarting the OTP flow
 * but user input should be retained for convenience.
 */
export const resetToPhoneEntry = (): void => {
    clearChannelAndOtpData();
    authStore$.validatePhoneType.set(undefined);
    authStore$.step.set('EnterPhoneNumber');
    authStore$.authMode.set('login');
    clearIdentityAuthData();
    // Intentionally preserves: initValidatePhoneData, pendingRegistrationInput
};

/**
 * Sync auth store with NextAuth session
 *
 * This function should be called when NextAuth session changes
 * to keep Legend-State store in sync
 *
 * @param session - NextAuth session data
 */
export const syncAuthWithSession = (
    session: {
        user?: CustomerProfile;
        token?: CustomerToken;
        expires_at?: number;
    } | null
): void => {
    authStore$.isSessionSyncing.set(true);

    if (session) {
        authStore$.customerProfile.set(session.user);
        authStore$.customerToken.set(session.token);
        authStore$.isAuthenticated.set(true);
        authStore$.sessionExpiresAt.set(session.expires_at);
    } else {
        clearAuthData();
    }

    authStore$.lastSessionSync.set(Date.now());
    authStore$.isSessionSyncing.set(false);
};
